import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/layout/Navbar";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import FeedPage from "@/pages/FeedPage";
import ProfilePage from "@/pages/ProfilePage";
import ScoutNavigatorPage from "@/pages/ScoutNavigatorPage";
import MessagesPage from "@/pages/MessagesPage";
import EventsPage from "@/pages/EventsPage";
import ConnectionsPage from "@/pages/ConnectionsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import SettingsPage from "@/pages/SettingsPage";
import TalentSpotlightPage from "@/pages/TalentSpotlightPage";
import PremiumPage from "@/pages/PremiumPage";
import TeamsPage from "@/pages/TeamsPage";
import AdminDashboard from "@/pages/AdminDashboard";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import SupportPage from "@/pages/SupportPage";
import SearchResultsPage from "@/pages/SearchResultsPage";
import ScoutDashboardPage from "@/pages/ScoutDashboardPage";
import MyTeamPage from "@/pages/MyTeamPage";
import SponsorDashboardPage from "@/pages/SponsorDashboardPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import ChooseRolePage from "@/pages/ChooseRolePage";

function ProtectedLayout() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-8 h-8 border-2 border-[#074572] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.role) return <Navigate to="/choose-role" replace />;
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="pt-16">
        <Outlet />
      </main>
    </div>
  );
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-8 h-8 border-2 border-[#074572] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to="/feed" replace />;
  return children;
}

function App() {
  return (
      <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/sponsor/:adId" element={<SponsorDashboardPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/choose-role" element={<ChooseRolePage />} />
          <Route element={<ProtectedLayout />}>
            <Route path="/feed" element={<FeedPage />} />
            <Route path="/profile/:id" element={<ProfilePage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/scout" element={<ScoutNavigatorPage />} />
            <Route path="/dashboard" element={<ScoutDashboardPage />} />
            <Route path="/team" element={<MyTeamPage />} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/spotlight" element={<TalentSpotlightPage />} />
            <Route path="/premium" element={<PremiumPage />} />
            <Route path="/teams" element={<TeamsPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/messages/:userId" element={<MessagesPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/connections" element={<ConnectionsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-center" richColors />
      </AuthProvider>
  );
}

export default App;
